import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { getGrowthStats } from '../data/statsData';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiArrowUpRight, HiClock, HiBriefcase, HiBuildingOffice,
  HiChevronRight, HiPhone, HiLightBulb, HiCpuChip,
  HiScissors, HiWrenchScrewdriver, HiShieldCheck, HiArrowRight, HiTruck,
  HiUserGroup, HiClipboardDocumentCheck,
  HiCube, HiMagnifyingGlass
} from 'react-icons/hi2';
import { FaUserShield, FaNetworkWired, FaServer, FaCogs, FaIndustry, FaHandshake, FaBolt, FaTools } from 'react-icons/fa';
import { GiCow, GiChicken } from 'react-icons/gi';

/* ---- Animated Count-Up with Intersection Observer ---- */
function CountUp({ end, suffix = '', duration = 2000 }) {
  const [count, setCount] = useState(0);
  const [hasStarted, setHasStarted] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasStarted) {
          setHasStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    let start = 0;
    const inc = end / (duration / 16);
    const timer = setInterval(() => {
      start += inc;
      if (start >= end) { clearInterval(timer); setCount(end); }
      else { setCount(Math.ceil(start)); }
    }, 16);
    return () => clearInterval(timer);
  }, [hasStarted, end, duration]);

  return <span ref={ref}>{count}{suffix}</span>;
}

/* ---- Section Header ---- */
function SectionHeader({ eyebrow, title, subtitle, light = false, center = true }) {
  return (
    <div className={`mb-14 ${center ? 'text-center max-w-3xl mx-auto' : ''}`}>
      <span className="eyebrow">{eyebrow}</span>
      <h2 className={`text-3xl md:text-4xl font-display font-extrabold leading-tight mb-4 ${light ? 'text-white' : 'text-heading'}`}>
        {title}
      </h2>
      {subtitle && (
        <p className={`text-base leading-relaxed ${light ? 'text-white/65' : 'text-body'}`}>
          {subtitle}
        </p>
      )}
    </div>
  );
}

/* ---- Country Flag Vector Component ---- */
function CountryFlag({ country, className = "w-full h-full" }) {
  if (country === 'China') {
    return (
      <svg className={className} viewBox="0 0 900 600" preserveAspectRatio="none">
        <rect width="900" height="600" fill="#de2910" />
        <polygon points="150,50 180,140 90,85 210,85 120,140" fill="#ffde00" />
        <polygon points="300,40 307,63 287,50 313,50 293,63" fill="#ffde00" />
        <polygon points="360,90 367,113 347,100 373,100 353,113" fill="#ffde00" />
        <polygon points="360,170 367,193 347,180 373,180 353,193" fill="#ffde00" />
        <polygon points="300,220 307,243 287,230 313,230 293,243" fill="#ffde00" />
      </svg>
    );
  }
  if (country === 'USA') {
    return (
      <svg className={className} viewBox="0 0 7410 3900" preserveAspectRatio="none">
        <rect width="7410" height="3900" fill="#b22234" />
        <path d="M0,450H7410M0,1050H7410M0,1650H7410M0,2250H7410M0,2850H7410M0,3450H7410" stroke="#fff" strokeWidth="300" />
        <rect width="2964" height="2100" fill="#3c3b6e" />
        <g fill="#fff">
          {[0, 1, 2, 3, 4].map(r => (
            <g key={r} transform={`translate(0, ${r * 420})`}>
              {[0, 1, 2, 3, 4, 5].map(c => (
                <circle key={c} cx={240 + c * 480} cy={210} r="70" />
              ))}
            </g>
          ))}
          {[0, 1, 2, 3].map(r => (
            <g key={r} transform={`translate(0, ${r * 420})`}>
              {[0, 1, 2, 3, 4].map(c => (
                <circle key={c} cx={480 + c * 480} cy={420} r="70" />
              ))}
            </g>
          ))}
        </g>
      </svg>
    );
  }
  if (country === 'Australia') {
    return (
      <svg className={className} viewBox="0 0 1280 640" preserveAspectRatio="none">
        <rect width="1280" height="640" fill="#00008b" />
        <g>
          <clipPath id="au-canton"><rect width="640" height="320" /></clipPath>
          <g clipPath="url(#au-canton)">
            <rect width="640" height="320" fill="#00247d" />
            <path d="M0,0 L640,320 M640,0 L0,320" stroke="#fff" strokeWidth="60" />
            <path d="M0,0 L640,320 M640,0 L0,320" stroke="#cf142b" strokeWidth="35" />
            <path d="M320,0 V320 M0,160 H640" stroke="#fff" strokeWidth="100" />
            <path d="M320,0 V320 M0,160 H640" stroke="#cf142b" strokeWidth="60" />
          </g>
        </g>
        <circle cx="320" cy="460" r="45" fill="#fff" />
        <circle cx="960" cy="520" r="30" fill="#fff" />
        <circle cx="830" cy="360" r="30" fill="#fff" />
        <circle cx="960" cy="140" r="30" fill="#fff" />
        <circle cx="1070" cy="270" r="30" fill="#fff" />
        <circle cx="1010" cy="400" r="18" fill="#fff" />
      </svg>
    );
  }
  if (country === 'Brazil') {
    return (
      <svg className={className} viewBox="0 0 1000 700" preserveAspectRatio="none">
        <rect width="1000" height="700" fill="#009c3b" />
        <polygon points="500,85 915,350 500,615 85,350" fill="#ffdf00" />
        <circle cx="500" cy="350" r="175" fill="#002776" />
        <path d="M330,360 Q500,310 670,360" stroke="#fff" strokeWidth="22" fill="none" />
      </svg>
    );
  }
  return null;
}

/* ---- Client Logos List ---- */
const clientLogosRaw = [
  "airtel-logo-png_seeklogo-288841.png",
  "atc tower.png",
  "Bharat_Sanchar_Nigam_Limited-Logo.wine.png",
  "download (1).png",
  "download (2).png",
  "download (3).png",
  "download (5).jpg",
  "download.png",
  "gni.jpg",
  "Hindustan_Petroleum-Logo.wine.png",
  "Hospitals-removebg-preview.png",
  "hyatt.png",
  "images (1).jpg",
  "images (1).png",
  "images (10).jpg",
  "images (10).png",
  "images (11).jpg",
  "images (11).png",
  "images (12).jpg",
  "images (12).png",
  "images (13).jpg",
  "images (13).png",
  "images (14).jpg",
  "images (14).png",
  "images (15).png",
  "images (16).png",
  "images (17).png",
  "images (18).png",
  "images (19).png",
  "images (2).jpg",
  "images (2).png",
  "images (20).png",
  "images (21).png",
  "images (22).png",
  "images (23).png",
  "images (25).png",
  "images (26).png",
  "images (27).png",
  "images (3).jpg",
  "images (3).png",
  "images (4).jpg",
  "images (4).png",
  "images (5).jpg",
  "images (5).png",
  "images (6).jpg",
  "images (6).png",
  "images (7).jpg",
  "images (7).png",
  "images (8).png",
  "images (9).jpg",
  "images (9).png",
  "images.jpg",
  "imagesss.jpg",
  "Indian_Army_Insignia_circular.png",
  "Indian_Oil_Corporation-Logo.wine.png",
  "Indus-Towers-Logo-Vector-300x300.jpg",
  "Jio-Logo.wine.png",
  "lemon_tree-removebg-preview.png",
  "maharashtra ploice.jpg",
  "Moreonly-logos.jpg",
  "rama international.jpg",
  "taiyo kagaku.png",
  "unnamed.png",
  "vlcc_660_061217063126.jpg"
];

const nameOverrides = {
  'airtel-logo-png_seeklogo-288841.png': 'Airtel',
  'Bharat_Sanchar_Nigam_Limited-Logo.wine.png': 'BSNL',
  'Hindustan_Petroleum-Logo.wine.png': 'Hindustan Petroleum',
  'Indian_Army_Insignia_circular.png': 'Indian Army',
  'Indian_Oil_Corporation-Logo.wine.png': 'Indian Oil',
  'Indus-Towers-Logo-Vector-300x300.jpg': 'Indus Towers',
  'Jio-Logo.wine.png': 'Jio',
  'lemon_tree-removebg-preview.png': 'Lemon Tree',
  'maharashtra ploice.jpg': 'Maharashtra Police',
  'Moreonly-logos.jpg': 'Moreonly',
  'rama international.jpg': 'Rama International',
  'taiyo kagaku.png': 'Taiyo Kagaku',
  'vlcc_660_061217063126.jpg': 'VLCC',
};

const clientLogos = clientLogosRaw.map(filename => {
  if (nameOverrides[filename]) {
    return {
      name: nameOverrides[filename],
      logo: `/Clients logo Green Gusto/${filename}`
    };
  }

  let name = filename
    .split('.')[0]
    .replace(/[-_]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Clean up common patterns
  name = name.replace(/Logo\.wine/gi, '')
    .replace(/Logo/gi, '')
    .replace(/Vector.*$/gi, '')
    .replace(/removebg.*$/gi, '')
    .replace(/\(\d+\)/g, '')
    .trim();

  name = name.split(' ')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
    .trim();

  if (!name || ['download', 'images', 'imagesss', 'unnamed'].includes(name.toLowerCase())) {
    name = 'Client Partner';
  }

  return {
    name,
    logo: `/Clients logo Green Gusto/${filename}`
  };
});

export default function Home() {
  const [activeProcessTab, setActiveProcessTab] = useState('sales');
  const [activeProductSlide, setActiveProductSlide] = useState(0);
  const [stats, setStats] = useState([]);

  useEffect(() => {
    // Add Elfsight script dynamically
    const script = document.createElement('script');
    script.src = 'https://elfsightcdn.com/platform.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Clean up the script tag when component unmounts
      const existingScript = document.querySelector('script[src="https://elfsightcdn.com/platform.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  useEffect(() => {
    const loadStats = async () => {
      const data = await getGrowthStats();
      setStats(data);
    };
    loadStats();
  }, []);

  const iconMap = {
    'Clock': <HiClock className="w-6 h-6" />,
    'Wrench': <HiWrenchScrewdriver className="w-6 h-6" />,
    'Clipboard': <HiClipboardDocumentCheck className="w-6 h-6" />,
    'Building': <HiBuildingOffice className="w-6 h-6" />
  };

  const productSlides = [
    {
      title: 'Eicher Silent Generators',
      brand: 'Eicher Engines',
      image: '/slide 1.jpg'
    },
    {
      title: 'TMTL Industrial Generators',
      brand: 'TMTL Engines',
      image: '/slide 2.jpg'
    }
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setActiveProductSlide(prev => (prev + 1) % 2);
    }, 7000);
    return () => clearInterval(timer);
  }, []);

  const salesJourneySteps = [
    { num: '01', title: 'Enquiry', desc: 'Enterprise client submits power specifications or custom load requirements.' },
    { num: '02', title: 'Site Visit', desc: 'Expert engineers inspect the layout, load characteristics, and installation site.' },
    { num: '03', title: 'Analysis', desc: 'Determine optimal generator sizing, synchronization needs, and fuel configurations.' },
    { num: '04', title: 'Quotation', desc: 'Provide transparent commercial and technical drafts with customized pricing options.' },
    { num: '05', title: 'Approval', desc: 'Final contract signoff, logistics scheduling, and pre-commissioning testing.' },
    { num: '06', title: 'Dispatch', desc: 'Insured delivery, on-site setup, cabling, acoustics validation, and handover.' },
  ];

  const maintenanceJourneySteps = [
    { num: '01', title: 'Complaint', desc: 'Customer registers issue on our 24/7 hotline or service desk portal.' },
    { num: '02', title: 'Inspection', desc: 'Remote diagnostics run or quick-response dispatch team deployed for checks.' },
    { num: '03', title: 'Planning', desc: 'Identify required spares, technical documentation, and safety protocols.' },
    { num: '04', title: 'Assignment', desc: 'Assign certified OEM-trained service engineers matching the generator make.' },
    { num: '05', title: 'Execution', desc: 'Repair, calibration, filter/lubricant check, and acoustic panel adjustment.' },
    { num: '06', title: 'Reporting', desc: 'Draft diagnostic report, logs, run-hour parameters, and fuel efficiency metrics.' },
    { num: '07', title: 'Completion', desc: 'Client sign-off, system re-commissioning, and ticket closure.' },
  ];

  const activeSteps = activeProcessTab === 'sales' ? salesJourneySteps : maintenanceJourneySteps;

  return (
    <div className="page-enter bg-light">

      {/* ========================================================
          SECTION 1: HERO VIDEO BACKGROUND
      ======================================================== */}
      <section className="relative min-h-[88vh] lg:min-h-screen flex items-center justify-center bg-charcoal text-white pt-16 overflow-hidden">

        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            autoPlay
            loop
            muted
            playsInline
            className="w-full h-full object-cover scale-[1.6] md:scale-[1.3] lg:scale-100 origin-center transition-transform duration-300"
          >
            <source src="/GG banner.mp4" type="video/mp4" />
          </video>
          <div className="absolute inset-0 z-10" style={{ background: 'linear-gradient(105deg, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.65) 50%, rgba(0, 0, 0, 0.3) 100%)' }} />
        </div>

        {/* Content */}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-20 w-full py-24 flex flex-col justify-center min-h-[75vh]">
          <div className="max-w-3xl">
            <motion.h1
              initial={{ opacity: 0, y: 28 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.1 }}
              className="text-4xl md:text-6xl lg:text-7xl font-display font-extrabold leading-[1.08] mb-6 tracking-tight"
            >
              Reliable Power.<br />
              <span className="text-gradient-gold">Industrial Excellence.</span>
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="text-base text-white/70 mb-10 max-w-2xl leading-relaxed"
            >
              Green Gusto Energy Pvt. Ltd. designs, installs, leases, and maintains heavy-duty industrial generator setups, solar projects, and custom electrical distribution panels.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="flex flex-col sm:flex-row gap-4"
            >
              <Link to="/contact#enquiry" className="btn-accent text-sm px-8 py-4 rounded-xl">
                Get a Quote
                <HiArrowUpRight className="w-4 h-4" />
              </Link>
              <a href="tel:+919609600600" className="btn-ghost text-sm px-8 py-4 rounded-xl">
                <HiPhone className="w-4 h-4" />
                Call Now
              </a>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 1.5: CLIENT LOGOS INFINITE MARQUEE
      ======================================================== */}
      <section className="py-12 bg-white border-b border-border overflow-hidden relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 text-center mb-6">
          <span className="eyebrow !mb-0 text-center">Trusted by Leading Enterprises & Networks</span>
        </div>

        {/* Infinite Scrolling Marquee Wrapper */}
        <div className="relative flex overflow-x-hidden w-full py-6 bg-white border-y border-border/60">
          {/* Shadow overlays to fade edges */}
          <div className="absolute inset-y-0 left-0 w-16 md:w-32 bg-gradient-to-r from-white to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-y-0 right-0 w-16 md:w-32 bg-gradient-to-l from-white to-transparent z-10 pointer-events-none" />

          {/* Marquee Content (duplicated to make it seamless) */}
          <div className="animate-marquee flex items-center gap-10 pr-10 whitespace-nowrap">
            {clientLogos.concat(clientLogos).map((logo, index) => (
              <div
                key={index}
                className="flex items-center justify-center transition-all duration-300 cursor-default hover:scale-[1.05] h-20 w-44 px-2"
              >
                <img
                  src={logo.logo}
                  alt={logo.name}
                  className="max-h-full max-w-full object-contain"
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 1.6: WHY CHOOSE GREEN GUSTO ENERGY?
      ======================================================== */}
      <section className="py-28 text-white relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0C1A12 0%, #0F2A1E 50%, #122A1C 100%)' }}>
        {/* Glow Effects */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none" style={{ background: 'rgba(28, 140, 91, 0.08)' }} />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none" style={{ background: 'rgba(245, 138, 31, 0.04)' }} />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12 items-start">

            {/* Left Sticky Column */}
            <div className="lg:sticky lg:top-28">
              <span className="eyebrow text-accent">Key Strengths</span>
              <h2 className="text-3xl md:text-5xl font-display font-extrabold leading-tight mb-6">
                Why Choose <br />
                <span className="text-gradient-gold">Green Gusto Energy?</span>
              </h2>
              <p className="text-base text-white/60 mb-8 leading-relaxed">
                We bridge the gap between heavy industrial power demands and premium, reliable delivery. Our authorized status and engineering-first culture make us the preferred partner for tier-1 enterprises.
              </p>

              <div className="space-y-4">
                <div className="flex items-center gap-4 py-3 border-y border-white/10">
                  <div className="text-2xl font-extrabold text-accent">15+</div>
                  <div className="text-xs text-white/60 uppercase tracking-wider font-semibold">Years of Industrial Expertise</div>
                </div>
              </div>
            </div>

            {/* Right Grid Column */}
            <div className="lg:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6">
              {[
                {
                  title: 'Experienced Technical Team',
                  desc: 'OEM-trained mechanical and electrical engineers certified by leading manufacturers like Cummins, Kirloskar, and Mahindra to handle complex setups.',
                  icon: <HiUserGroup className="w-6 h-6 text-accent" />
                },
                {
                  title: 'End-to-End Energy Solutions',
                  desc: 'Complete lifecycle coverage from site selection and custom load analysis to solar panel setups, LT/HT distribution panels, and AMC operations.',
                  icon: <FaNetworkWired className="w-6 h-6 text-accent" />
                },
                {
                  title: 'Authorized Generator & Supplier',
                  desc: 'Direct factory access as an authorized supplier of industrial generators and specialized heavy components, securing full OEM warranty benefits.',
                  icon: <HiBuildingOffice className="w-6 h-6 text-accent" />
                },
                {
                  title: 'High-Quality Installation & Maintenance',
                  desc: 'Rigorous engineering checklists, standard-complying acoustics, custom panel syncs, and comprehensive scheduled preventive services.',
                  icon: <HiWrenchScrewdriver className="w-6 h-6 text-accent" />
                },
                {
                  title: 'Fast Response & Reliable Support',
                  desc: 'A dedicated service desk committed to under-2-hour onsite response times supported by regional service vehicle clusters.',
                  icon: <HiClock className="w-6 h-6 text-accent" />
                },
                {
                  title: 'Customized Industrial Solutions',
                  desc: 'Sizing, synchronization panels, and custom power packages built exactly around high-load manufacturing, hotels, and telecom setups.',
                  icon: <HiCpuChip className="w-6 h-6 text-accent" />
                },
                {
                  title: 'Safety, Quality & Sustainability',
                  desc: 'Strict adherence to ISO guidelines, safe fuel and exhaust routing, noise-dampening enclosures, and carbon-offsetting solar setups.',
                  icon: <HiShieldCheck className="w-6 h-6 text-accent" />
                }
              ].map((strength, index) => (
                <div
                  key={index}
                  className={`border border-white/6 hover:border-accent/30 rounded-2xl p-6 hover:-translate-y-1 transition-all duration-300 group ${index === 6 ? 'md:col-span-2' : ''
                    }`}
                  style={{ background: 'rgba(15, 90, 58, 0.06)' }}
                >
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center border border-white/10 group-hover:border-accent/30 transition-all duration-300 mb-5" style={{ background: 'rgba(245, 138, 31, 0.08)' }}>
                    {strength.icon}
                  </div>
                  <h3 className="text-lg font-display font-bold text-white mb-2 transition-colors">
                    {strength.title}
                  </h3>
                  <p className="text-xs text-white/50 leading-relaxed">
                    {strength.desc}
                  </p>
                </div>
              ))}
            </div>

          </div>
        </div>
      </section>



      {/* ========================================================
          SECTION 2: BUSINESS DIVISIONS BENTO GRID
      ======================================================== */}
      <section className="py-28 max-w-7xl mx-auto px-6 lg:px-10">
        <SectionHeader
          eyebrow="Industrial Focus"
          title="Four Pillars of Energy & Engineering"
          subtitle="Our multi-divisional approach allows us to deliver optimized synergy across corporate solar deployment, quiet silent diesel generators, distribution switchgear panel units, and robust tooling systems."
        />

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

          {/* Generator Solutions — Large */}
          <div className="md:col-span-2 rounded-3xl p-8 border relative overflow-hidden shadow-card group flex flex-col justify-between min-h-[380px] transition-all duration-300 hover:-translate-y-1 hover:shadow-premium-hover bg-gradient-to-br from-[#EAF4EE] to-[#D5EADF]" style={{ borderColor: 'rgba(15, 90, 58, 0.15)' }}>
            <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
            <div className="absolute -bottom-12 -right-12 w-40 h-40 bg-accent/5 rounded-full blur-2xl" />

            <div className="relative z-10 flex justify-between items-start">
              <div className="icon-box-lg card-icon">
                <HiWrenchScrewdriver className="w-7 h-7" />
              </div>
              <span className="badge-primary text-[10px]">Primary Vertical</span>
            </div>

            <div className="relative z-10 mt-8">
              <h3 className="text-2xl font-display font-extrabold text-heading mb-3">Generator Solutions</h3>
              <p className="text-sm text-body leading-relaxed mb-6 max-w-xl">
                Providing complete turnkey silent generator setups from sales and rental configurations to standard AMC/CAMC packages. Backed by certified technicians and genuine OEM spare parts inventory.
              </p>
              <div className="flex flex-wrap gap-2">
                {['Sales (10kVA - 3000kVA)', 'Rentals (24x7 Backup)', 'AMC & CAMC Contracts'].map(tag => (
                  <span key={tag} className="badge-primary text-[10px] px-3 py-1.5">{tag}</span>
                ))}
              </div>
            </div>

            <div className="relative z-10 mt-6 flex justify-between items-center pt-6 border-t border-primary/10">
              <span className="text-xs font-semibold text-primary">Learn More about Gensets</span>
              <Link to="/generator-sales" className="w-9 h-9 rounded-full bg-primary/10 hover:bg-primary flex items-center justify-center text-primary hover:text-white transition-all duration-200 hover:shadow-premium group-hover:scale-105">
                <HiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Solar Solutions */}
          <div className="rounded-3xl p-8 border shadow-card group flex flex-col justify-between min-h-[380px] transition-all duration-300 hover:-translate-y-1 hover:shadow-premium-hover bg-gradient-to-br from-[#FFF5E6] to-[#FFE5C4]" style={{ borderColor: 'rgba(245, 138, 31, 0.15)' }}>
            <div className="relative z-10">
              <div className="icon-box-lg card-icon">
                <HiLightBulb className="w-7 h-7" />
              </div>
            </div>

            <div className="relative z-10 mt-8">
              <h3 className="text-xl font-display font-extrabold text-heading mb-3">Solar Power Solutions</h3>
              <p className="text-sm text-body leading-relaxed mb-6">
                On-grid, off-grid, and hybrid solar installations for industrial factories, hotels, infrastructure setups, and residential complexes. Reduced carbon footprint and peak grid tariffs.
              </p>
              <ul className="space-y-2">
                {['EPC Installation Setup', 'Net Metering Approval'].map(item => (
                  <li key={item} className="flex items-center gap-2 text-xs text-body font-semibold">
                    <span className="w-1.5 h-1.5 rounded-full bg-accent flex-shrink-0" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="relative z-10 mt-6 flex justify-between items-center pt-6 border-t border-accent/10">
              <span className="text-xs font-semibold text-accent-dark">Browse Solar</span>
              <Link to="/solar-solutions" className="w-9 h-9 rounded-full bg-accent/10 hover:bg-accent flex items-center justify-center text-accent-dark hover:text-white transition-all duration-200">
                <HiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Electrical Panels */}
          <div className="rounded-3xl p-8 border shadow-card group flex flex-col justify-between min-h-[340px] transition-all duration-300 hover:-translate-y-1 hover:shadow-premium-hover bg-gradient-to-br from-[#F0F4FA] to-[#DCE6F2]" style={{ borderColor: 'rgba(79, 121, 184, 0.15)' }}>
            <div className="relative z-10">
              <div className="icon-box-lg card-icon">
                <HiCpuChip className="w-7 h-7" />
              </div>
            </div>

            <div className="relative z-10 mt-8">
              <h3 className="text-xl font-display font-extrabold text-heading mb-3">Electrical Panels</h3>
              <p className="text-sm text-body leading-relaxed mb-6">
                Custom engineered LT/HT panels, AMF synchronizing panel boards, capacitor banks, and power distribution modules designed for heavy factories.
              </p>
            </div>

            <div className="relative z-10 mt-auto flex justify-between items-center pt-5 border-t border-secondary/15">
              <span className="text-xs font-semibold text-secondary-dark">Explore Electrical Panels</span>
              <Link to="/electrical-panels" className="w-9 h-9 rounded-full bg-secondary/15 hover:bg-secondary flex items-center justify-center text-secondary-dark hover:text-white transition-all duration-200">
                <HiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Cutting Tools — Large */}
          <div className="md:col-span-2 rounded-3xl p-8 border shadow-card group flex flex-col justify-between min-h-[340px] transition-all duration-300 hover:-translate-y-1 hover:shadow-premium-hover bg-gradient-to-br from-[#F4F6F5] to-[#E3E8E6]" style={{ borderColor: 'rgba(15, 90, 58, 0.10)' }}>
            <div className="relative z-10">
              <div className="icon-box-lg card-icon">
                <HiScissors className="w-7 h-7" />
              </div>
            </div>

            <div className="relative z-10 mt-8">
              <h3 className="text-xl font-display font-extrabold text-heading mb-3">Cutting Tools</h3>
              <p className="text-sm text-body leading-relaxed mb-6 max-w-xl">
                Official distribution partners for high-performance precision tooling, carbon alloy drills, carbide cutters, and machine gear accessories supporting automotive and metal fabrication plants.
              </p>
            </div>

            <div className="relative z-10 mt-auto flex justify-between items-center pt-6 border-t border-primary/10">
              <span className="text-xs font-semibold text-primary">Browse Cutting Tools</span>
              <Link to="/cutting-tools" className="w-9 h-9 rounded-full bg-primary/10 hover:bg-primary flex items-center justify-center text-primary hover:text-white transition-all duration-200">
                <HiArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 3: GROWTH STATISTICS
      ======================================================== */}
      <section className="py-20 bg-light-section border-y border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <div className="text-center max-w-3xl mx-auto mb-14">
            <span className="eyebrow">Proven Track Record</span>
            <h2 className="text-3xl md:text-4xl font-display font-extrabold text-heading">Green Gusto in Numbers</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((s, i) => (
              <div key={s.id || i} className="stat-card group">
                <div className="icon-box card-icon mx-auto mb-5">
                  {iconMap[s.icon] || <HiClock className="w-6 h-6" />}
                </div>
                <div className="text-4xl lg:text-5xl font-extrabold font-display text-heading group-hover:text-primary transition-colors duration-300 mb-2">
                  <CountUp end={Number(s.num) || 0} suffix={s.suffix} />
                </div>
                <h4 className="text-xs font-bold text-heading uppercase tracking-wider mb-2">{s.label}</h4>
                <p className="text-xs text-body-muted leading-relaxed">{s.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION: GLOBAL SOURCING & PROCUREMENT
      ======================================================== */}
      <section className="py-24 bg-gradient-to-b from-[#F8FAF9] via-white to-[#F0F5F2] border-b border-border relative overflow-hidden">
        {/* Ambient atmospheric glows */}
        <div className="absolute top-12 left-1/4 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[130px] pointer-events-none" />
        <div className="absolute bottom-16 right-1/4 w-[450px] h-[450px] bg-accent/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-1/2 right-4 w-72 h-72 bg-secondary/5 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10 relative z-10">

          {/* PART 1: OUR GLOBAL SOURCING NETWORK */}
          <div className="mb-20">
            <div className="text-center max-w-3xl mx-auto mb-12">
              <span className="eyebrow">International Corridors</span>
              <h3 className="text-2xl md:text-3xl lg:text-4xl font-display font-extrabold text-heading">
                Our Global Sourcing Network
              </h3>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { country: "China" },
                { country: "USA" },
                { country: "Australia" },
                { country: "Brazil" },
              ].map((c, i) => (
                <div
                  key={i}
                  className="bg-white rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1.5 group relative overflow-hidden flex flex-col items-center justify-center text-center"
                >
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                  <div className="w-16 h-11 rounded-xl overflow-hidden border border-border/90 shadow-sm flex items-center justify-center mb-3.5 group-hover:scale-105 transition-transform duration-300">
                    <CountryFlag country={c.country} className="w-full h-full object-cover" />
                  </div>

                  <h4 className="text-lg md:text-xl font-display font-extrabold text-heading">
                    {c.country}
                  </h4>
                </div>
              ))}
            </div>
          </div>

          {/* PART 2: OUR PROCUREMENT PROCESS */}
          <div className="mb-20">
            <div className="text-center max-w-3xl mx-auto mb-14">
              <span className="eyebrow">Structured Execution</span>
              <h3 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-3">
                Our Procurement Process
              </h3>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative">
              {[
                {
                  num: "01",
                  title: "Supplier Identification",
                  desc: "Identify reliable and suitable international suppliers based on your requirements.",
                  icon: <HiMagnifyingGlass className="w-6 h-6" />,
                },
                {
                  num: "02",
                  title: "Product Sourcing & Negotiation",
                  desc: "Source the right products and negotiate competitive pricing with suppliers.",
                  icon: <FaHandshake className="w-6 h-6" />,
                },
                {
                  num: "03",
                  title: "Import Coordination",
                  desc: "Coordinate the import process, documentation and logistics for a smoother procurement experience.",
                  icon: <HiTruck className="w-6 h-6" />,
                },
                {
                  num: "04",
                  title: "End-to-End Procurement Support",
                  desc: "Manage the procurement journey from sourcing and supplier coordination to delivery support.",
                  icon: <HiShieldCheck className="w-6 h-6" />,
                },
              ].map((step, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-2xl p-6 border border-border shadow-card hover:shadow-card-hover transition-all duration-300 hover:-translate-y-1.5 group relative flex flex-col"
                >
                  <div className="flex items-center justify-between mb-5">
                    <span className="text-3xl font-display font-black text-primary/20 group-hover:text-primary transition-colors duration-300">
                      {step.num}
                    </span>
                    <div className="w-12 h-12 rounded-xl bg-primary/10 text-primary group-hover:bg-primary group-hover:text-white transition-all duration-300 flex items-center justify-center shadow-sm">
                      {step.icon}
                    </div>
                  </div>

                  <h4 className="text-base font-bold text-heading mb-2.5 font-display group-hover:text-primary transition-colors">
                    {step.num} — {step.title}
                  </h4>

                  <p className="text-xs text-body leading-relaxed">
                    {step.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>

          {/* PART 3: PRODUCTS WE SOURCE */}
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-border shadow-elevated relative overflow-hidden">
            <div className="absolute top-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-[80px] pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-accent/5 rounded-full blur-[60px] pointer-events-none" />

            <div className="relative z-10">
              <div className="text-center max-w-3xl mx-auto mb-10">
                <span className="eyebrow">Comprehensive Coverage</span>
                <h3 className="text-2xl md:text-3xl font-display font-extrabold text-heading mb-3">
                  Products We Source
                </h3>
              </div>

              {/* 6 Category Cards Grid - 2 rows with 3 cards per row */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[
                  { name: "Industrial Products", icon: <FaIndustry className="w-6 h-6" /> },
                  { name: "Mechanical Components", icon: <FaCogs className="w-6 h-6" /> },
                  { name: "Electrical Products", icon: <FaBolt className="w-6 h-6" /> },
                  { name: "Power Products", icon: <HiLightBulb className="w-6 h-6" /> },
                  { name: "Engineering Equipment", icon: <FaTools className="w-6 h-6" /> },
                  { name: "Specialized Industrial Components", icon: <HiCube className="w-6 h-6" /> },
                ].map((cat, i) => (
                  <div
                    key={i}
                    className="bg-white hover:bg-light-section/30 py-8 px-6 md:py-10 md:px-8 rounded-2xl border border-border hover:border-primary/40 transition-all duration-300 shadow-card hover:shadow-card-hover hover:-translate-y-1.5 group text-center flex flex-col items-center justify-center relative overflow-hidden"
                  >
                    <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-accent to-primary opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                    <div className="w-14 h-14 rounded-2xl bg-light-section group-hover:bg-primary text-primary group-hover:text-white transition-all duration-300 flex items-center justify-center mb-4 shadow-sm border border-border/80 group-hover:scale-105 flex-shrink-0">
                      {cat.icon}
                    </div>

                    <h5 className="text-base md:text-lg font-display font-extrabold text-heading group-hover:text-primary transition-colors leading-snug">
                      {cat.name}
                    </h5>
                  </div>
                ))}
              </div>
            </div>
          </div>

        </div>
      </section>

      {/* ========================================================
          SECTION 1.7: EICHER & TMTL GENERATORS IMAGE SHOWCASE
      ======================================================== */}
      <section className="py-20 bg-white border-y border-border relative overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute top-0 right-0 w-80 h-80 bg-primary/5 rounded-full blur-[80px]" />

        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Core Product Showcase"
            title="Eicher & TMTL Generators"
            subtitle="Explore our visual product showcase highlighting the design and builds of our industry-leading Eicher and TMTL diesel generators."
          />

          {/* Interactive Selection Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {productSlides.map((slide, idx) => (
              <button
                key={idx}
                onClick={() => setActiveProductSlide(idx)}
                className={`px-6 py-3 rounded-2xl flex flex-col items-start border text-left transition-all duration-300 w-56 ${activeProductSlide === idx
                  ? 'border-primary bg-primary/5 shadow-premium text-heading ring-2 ring-primary/10'
                  : 'border-border bg-white text-body hover:border-primary/40'
                  }`}
              >
                <span className="text-[9px] uppercase font-bold tracking-wider text-accent">{slide.brand}</span>
                <span className="text-xs font-extrabold font-display mt-0.5">{slide.title}</span>
              </button>
            ))}
          </div>

          {/* Sliding Showcase Frame */}
          {productSlides.map((slide, idx) => {
            if (idx !== activeProductSlide) return null;
            return (
              <div key={idx} className="flex flex-col items-center">

                {/* Image Container */}
                <div className="relative w-full max-w-5xl aspect-[16/9] md:aspect-[21/9] rounded-3xl overflow-hidden shadow-card border border-border group">
                  <AnimatePresence mode="wait">
                    <motion.div
                      key={activeProductSlide}
                      initial={{ opacity: 0, scale: 0.98 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ duration: 0.4 }}
                      className="absolute inset-0 w-full h-full"
                    >
                      <img
                        src={slide.image}
                        alt={slide.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent z-10" />

                      {/* Premium Floating Caption Card */}
                      <div className="absolute bottom-6 left-6 md:bottom-10 md:left-10 z-20 bg-white/95 backdrop-blur-md border border-border/80 px-6 py-3.5 rounded-2xl shadow-premium">
                        <span className="text-[10px] uppercase font-bold text-accent tracking-wider block">{slide.brand} Series</span>
                        <span className="text-sm md:text-base font-extrabold font-display text-heading mt-0.5 block">{slide.title}</span>
                      </div>
                    </motion.div>
                  </AnimatePresence>
                </div>

                {/* Slider Progress Dots */}
                <div className="flex gap-2.5 mt-8">
                  {productSlides.map((_, dotIdx) => (
                    <button
                      key={dotIdx}
                      onClick={() => setActiveProductSlide(dotIdx)}
                      className={`h-1.5 rounded-full transition-all duration-300 ${activeProductSlide === dotIdx ? 'bg-primary w-8' : 'bg-border w-1.5 hover:bg-body/30'
                        }`}
                      aria-label={`Go to slide ${dotIdx + 1}`}
                    />
                  ))}
                </div>

              </div>
            );
          })}
        </div>
      </section>

      {/* Section 4 (Why Choose Us) has been consolidated into the premium "Why Choose Green Gusto Energy?" section above */}

      {/* ========================================================
          SECTION 5: INDUSTRIES WE SERVE
      ======================================================== */}
      <section className="py-28 bg-light-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Market Presence"
            title="Powering Key Industries Across the Nation"
            subtitle="Whether it is clean solar power grids or massive multi-megawatt diesel backup setups, we support critical operations across diverse industries."
          />

          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
            {[
              { title: 'Telecom Network', desc: 'Cell towers & nodes', icon: <FaNetworkWired className="w-5 h-5" /> },
              { title: 'Manufacturing', desc: 'Continuous production', icon: <FaCogs className="w-5 h-5" /> },
              { title: 'Hospitals', desc: 'Life-saving power backup', icon: <HiShieldCheck className="w-5 h-5" /> },
              { title: 'Hotels & Leisure', desc: 'Uninterrupted guest comfort', icon: <HiBuildingOffice className="w-5 h-5" /> },
              { title: 'Edu Institutes', desc: 'Campuses & datacenters', icon: <HiUserGroup className="w-5 h-5" /> },
              { title: 'Government Org', desc: 'Critical infrastructure', icon: <HiClipboardDocumentCheck className="w-5 h-5" /> },
              { title: 'Petrol Pumps', desc: 'Fuel retailing points', icon: <HiTruck className="w-5 h-5" /> },
              { title: 'Infrastructure', desc: 'Metro & highway builds', icon: <HiBriefcase className="w-5 h-5" /> },
              { title: 'Factories', desc: 'Heavy engineering shops', icon: <FaServer className="w-5 h-5" /> },
              { title: 'Commercial Bldgs', desc: 'IT parks & malls', icon: <HiBuildingOffice className="w-5 h-5" /> },
              { title: 'Agriculture', desc: 'Pumping & processing', icon: <HiLightBulb className="w-5 h-5" /> },
              { title: 'Automotive', desc: 'Assembly operations', icon: <FaUserShield className="w-5 h-5" /> },
              { title: 'Dairy', desc: 'Milking & cold chain', icon: <GiCow className="w-5 h-5" /> },
              { title: 'Poultry Farm', desc: 'Automated sheds & feed', icon: <GiChicken className="w-5 h-5" /> },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-2xl p-5 border border-border shadow-card hover:shadow-card-hover hover:-translate-y-1.5 hover:border-primary/20 transition-all duration-300 group">
                <div className="w-10 h-10 rounded-xl card-icon flex items-center justify-center mb-4">
                  {item.icon}
                </div>
                <h4 className="text-xs font-bold text-heading mb-1 leading-tight">{item.title}</h4>
                <p className="text-[10px] text-body-muted">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 6: PROCESS TIMELINE
      ======================================================== */}
      <section className="pt-28 pb-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeader
            eyebrow="Structured Workflows"
            title="How We Partner With You"
          />

          {/* Tab Selector */}
          <div className="flex justify-center gap-3 mb-14">
            {['sales', 'maintenance'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveProcessTab(tab)}
                className={`px-7 py-3 rounded-full text-xs font-bold tracking-wider uppercase transition-all duration-300 ${activeProcessTab === tab
                  ? 'bg-primary text-white shadow-premium'
                  : 'bg-light-section text-body hover:text-heading border border-border'
                  }`}
              >
                {tab === 'sales' ? 'Sales Journey' : 'Maintenance Journey'}
              </button>
            ))}
          </div>

          {/* Timeline Cards */}
          <div className="overflow-x-auto pb-6">
            <div className={`flex gap-5 min-w-max px-2`}>
              <AnimatePresence mode="wait">
                {activeSteps.map((step, idx) => (
                  <motion.div
                    key={`${activeProcessTab}-${idx}`}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, delay: idx * 0.05 }}
                    className="w-60 flex-shrink-0 bg-white border border-border rounded-2xl p-6 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 relative group"
                  >
                    <div className="text-4xl font-extrabold font-display text-primary/15 group-hover:text-primary/25 transition-colors mb-4">
                      {step.num}
                    </div>
                    <h4 className="text-sm font-bold text-heading mb-2">{step.title}</h4>
                    <p className="text-xs text-body leading-relaxed">{step.desc}</p>
                    {idx < activeSteps.length - 1 && (
                      <div className="absolute top-1/2 -right-3 -translate-y-1/2 z-10 hidden md:flex w-6 h-6 items-center justify-center text-border-dark">
                        <HiChevronRight className="w-4 h-4" />
                      </div>
                    )}
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </section>

      {/* Section 7 (Clients Logo Wall) has been consolidated into the premium horizontal marquee above */}

      {/* ========================================================
          SECTION 8: TESTIMONIALS (Google My Business Reviews)
      ======================================================== */}
      <section className="pt-16 pb-28 bg-light-section border-t border-b border-border/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          {/* Google My Business Reviews Widget */}
          <div>
            <div className="elfsight-app-050d530c-8058-4643-a8b2-e9ea0aaaa169" data-elfsight-app-lazy="" />
          </div>
        </div>
      </section>

      {/* ========================================================
          SECTION 9: CTA BANNER
      ======================================================== */}
      <section className="text-white py-24 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #0C1A12 0%, #0F5A3A 50%, #0A3F29 100%)' }}>
        {/* Decorative elements */}
        <div className="absolute -top-20 -right-20 w-80 h-80 bg-accent/10 rounded-full blur-[80px]" />
        <div className="absolute -bottom-16 -left-16 w-64 h-64 bg-white/4 rounded-full blur-[60px]" />
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

        <div className="max-w-5xl mx-auto px-6 lg:px-10 relative z-10 text-center">


          <h2 className="text-3xl md:text-5xl font-display font-extrabold mb-6 leading-tight">
            Need Reliable Power Infrastructure?
          </h2>
          <p className="text-base text-white/65 mb-10 max-w-xl mx-auto leading-relaxed">
            Discuss custom generator rentals, long-term AMC support, solar setups, or electrical installations with our application engineers.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact#enquiry" className="btn-accent px-8 py-4 rounded-xl text-sm">
              Get a Free Consultation
              <HiArrowUpRight className="w-4 h-4" />
            </Link>
            <a href="tel:+919609600600" className="btn-ghost px-8 py-4 rounded-xl text-sm">
              <HiPhone className="w-4 h-4" />
              Call +91 96096 00600
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}
